import {Anchor, createTopAnchor} from '@cypro/ui/components/ui/infinite-data-table'
import {queries} from '@/shared/integrations/zero'
import {ListSolutionsInput} from '@cypro/engine/queries'
import {DrugCatalogItem} from '@cypro/engine/schema'
import {useQuery} from '@rocicorp/zero/react'
import {useEffect, useMemo, useState} from 'react'

/**
 * If you want to filter solution types, use the `SolutionType` enum exported from `@cypro/engine`, and include the desired types in the `filters.types` array.
 *
 * Eg. `filters: { types: [SolutionType.enum.SOLVENT] }`
 *
 * The `types` array also receives `uuidv7`, which is used to filter directly using the ID of the solution type, but not really human friendly.
 */
export function useGetSolutionDrugs({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit,
}: Omit<Partial<ListSolutionsInput>, 'cursor'> = {}) {
  const [anchor, setAnchor] = useState<Anchor<DrugCatalogItem>>(createTopAnchor<DrugCatalogItem>())

  const cursor =
    anchor.startRow?.id && anchor.startRow?.createdAt
      ? {
          id: anchor.startRow.id,
          direction: anchor.direction,
          createdAt: anchor.startRow.createdAt,
          name: anchor.startRow.name,
        }
      : undefined

  const query = queries.drugCatalog.listSolutions({
    cursor,
    sort,
    filters: {status: ['active'] as const, ...filters},
    limit,
  })
  const [solutions, meta] = useQuery(query)

  const filtersHash = useMemo(() => JSON.stringify(filters), [filters])
  useEffect(() => {
    setAnchor(createTopAnchor<DrugCatalogItem>())
  }, [sort?.dir, sort?.field, filtersHash, limit])

  return {
    query,
    meta,
    solutions,
    anchor,
    fetchMore: setAnchor,
    canFetchMore: solutions.length === limit,
    isFetching: meta.type === 'unknown',
  }
}
